import React, { useState } from 'react';
import { 
  BookOpen, 
  DollarSign, 
  Split, 
  Settings, 
  CheckCircle, 
  Sparkles, 
  ShieldCheck, 
  Gift, 
  HelpCircle,
  Activity,
  ArrowRight,
  TrendingUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type TabId = 'workflow' | 'architecture' | 'benchmarks' | 'referrals' | 'faq';

export const TipFlowGuide: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('workflow');

  const workflowSections = [
    {
      title: '1. POS Integration & Review',
      icon: <DollarSign className="w-5 h-5 text-blue-600" />,
      instructions: 'Import point-of-sale tip files via CSV. New transactions land in the review queue. Operators double-verify shift hours and POS IDs, then Accept & Approve to lock records into the immutable ledger.',
    },
    {
      title: '2. Cash & Jar Submission',
      icon: <Split className="w-5 h-5 text-purple-600" />,
      instructions: 'Staff members input cash tips or contribute to collective jar pools directly. TipValue isolates these from credit transactions while leveraging actual scheduling logs for precise split weight distribution.',
    },
    {
      title: '3. Strategic Pool Allocation',
      icon: <Settings className="w-5 h-5 text-amber-600" />,
      instructions: 'Tips are divided instantly according to your designated store rules (Equal Split or Role Weighting). Central calculations maintain precision down to the last decimal, reducing billing disputes entirely.',
    },
    {
      title: '4. Finalized Payroll Export',
      icon: <CheckCircle className="w-5 h-5 text-emerald-600" />,
      instructions: 'After validating the ledger matching status, export compliant files tailored for major payroll systems (ADP Run, etc.). Only fully verified, un-disputed records are consolidated for payout.',
    }
  ];

  return (
    <div className="space-y-6 font-sans text-gray-800">
      
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-950 text-white rounded-3xl p-6 relative overflow-hidden shadow-xl border border-slate-800">
        <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none rotate-12">
          <BookOpen size={180} />
        </div>
        
        <div className="relative z-10 space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-blue-500/20 text-blue-300 text-[9px] font-black uppercase px-2 py-0.5 rounded border border-blue-500/30">
              Interactive Documentation
            </span>
            <div className="text-xs text-indigo-400 font-bold flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Operators Handbook v2.4
            </div>
          </div>
          <h2 className="text-2xl font-black uppercase tracking-tight italic">
            TipValue AI Knowledge Commons
          </h2>
          <p className="text-gray-300 text-xs max-w-xl leading-relaxed font-semibold">
            Understand key transactional rules, verify execution benchmarks, and learn how our single source of truth supports team equity.
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1.5 rounded-2xl border border-gray-200/60">
        <button
          onClick={() => setActiveTab('workflow')}
          className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTab === 'workflow' 
              ? 'bg-white text-gray-900 shadow-sm' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <Activity className="w-3.5 h-3.5" />
          General Workflow
        </button>
        <button
          onClick={() => setActiveTab('architecture')}
          className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTab === 'architecture' 
              ? 'bg-white text-gray-900 shadow-sm' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          Ledger Rules & Wizard
        </button>
        <button
          onClick={() => setActiveTab('benchmarks')}
          className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTab === 'benchmarks' 
              ? 'bg-white text-gray-900 shadow-sm' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          Tipping Benchmarks
        </button>
        <button
          onClick={() => setActiveTab('referrals')}
          className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTab === 'referrals' 
              ? 'bg-white text-gray-900 shadow-sm' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <Gift className="w-3.5 h-3.5" />
          Referral Loop ($50 Credit)
        </button>
        <button
          onClick={() => setActiveTab('faq')}
          className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold uppercase tracking-wider rounded-xl transition-all cursor-pointer ${
            activeTab === 'faq' 
              ? 'bg-white text-gray-900 shadow-sm' 
              : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          <HelpCircle className="w-3.5 h-3.5" />
          FAQ Handbook
        </button>
      </div>

      {/* Main Panel Content Area */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
          className="space-y-6"
        >
          
          {/* TAB 1: WORKFLOW */}
          {activeTab === 'workflow' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl">
                <h3 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                  <Activity className="w-4 h-4 animate-pulse" /> Alignment Strategy Note
                </h3>
                <p className="text-xs text-blue-800 leading-relaxed font-semibold">
                  TipValue divides pooled calculations instantly using store-wide guidelines. Verification settings dictate how elements route. Check strategy constraints prior to uploading shift CSV summaries to prevent transaction mapping issues.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {workflowSections.map((section, idx) => (
                  <div key={idx} className="p-4 bg-white rounded-2xl border border-gray-200 hover:border-gray-300 shadow-sm flex gap-4 transition-all">
                    <div className="bg-gray-50 p-2.5 rounded-xl h-fit border border-gray-100 shadow-sm self-start shrink-0">
                      {section.icon}
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-extrabold text-gray-900 text-xs uppercase tracking-tight">{section.title}</h4>
                      <p className="text-[11px] text-gray-550 leading-relaxed font-medium">{section.instructions}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 2: LEDGER RULES & WIZARD */}
          {activeTab === 'architecture' && (
            <div className="space-y-5 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
              <div className="space-y-1.5 border-b border-gray-100 pb-4">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" /> Immutable Ledger Rigor
                </h3>
                <p className="text-[11px] text-gray-500 font-medium">
                  We enforce a strict code of ledger verification to validate that your business is audit-ready at any second.
                </p>
              </div>

              <div className="space-y-4 text-xs font-medium text-gray-600">
                <div className="p-3.5 bg-slate-50 rounded-xl border border-gray-200/60 space-y-1.5">
                  <div className="text-gray-900 font-bold uppercase tracking-tight text-xs flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> No Inline Screen Filters Allowed
                  </div>
                  <p className="leading-relaxed text-[11px] font-semibold text-gray-550">
                    Calculations must never be filtered asynchronously on visual UI components. All pages adhere to a single source of truth inside our global validation helper suite (<code className="text-rose-600 font-bold bg-rose-50 px-1 py-0.5 rounded text-[10px]">src/lib/transactionUtils.ts</code>).
                  </p>
                </div>

                <div className="p-3.5 bg-slate-50 rounded-xl border border-gray-200/60 space-y-1.5">
                  <div className="text-gray-900 font-bold uppercase tracking-tight text-xs flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> On-Write Field Population
                  </div>
                  <p className="leading-relaxed text-[11px] font-semibold text-gray-550">
                    To prevent browser guessing or runtime drift, every API importer must write explicit transaction schemas detailing fields like <code className="text-blue-600 font-bold bg-blue-50 px-1 py-0.5 rounded text-[10px]">reportingStatus</code>, <code className="text-blue-600 font-bold bg-blue-50 px-1 py-0.5 rounded text-[10px]">type</code>, and <code className="text-blue-600 font-bold bg-blue-50 px-1 py-0.5 rounded text-[10px]">source</code>.
                  </p>
                </div>

                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl space-y-2">
                  <div className="text-blue-900 font-black uppercase text-xs flex items-center gap-1">
                    <Sparkles className="w-4 h-4 text-blue-600" /> Interactive Onboarding Activation Wizard
                  </div>
                  <p className="text-[11px] text-blue-800 leading-relaxed font-semibold">
                    Are you testing out TipValue AI for the first time? Navigate to the **Business Profile** tab inside Settings. Use the simple setup flow to quickly establish your core allocation settings, loads template workforce personnel, and performs complete calculation rehearsals instantly!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB: BENCHMARKS INFORMATION */}
          {activeTab === 'benchmarks' && (
            <div className="space-y-5 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm animate-in fade-in duration-200">
              <div className="space-y-1 border-b border-gray-100 pb-4">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4 text-indigo-600" /> Peer Benchmarks
                </h3>
                <p className="text-[11px] text-gray-500 font-medium font-sans">
                  Learn how TipValue compares your team's performance with local business averages.
                </p>
              </div>

              <div className="space-y-4 text-xs font-medium text-gray-600">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3.5 bg-slate-50 rounded-xl border border-gray-200/60 space-y-1">
                    <div className="text-gray-900 font-bold uppercase tracking-tight text-xs flex items-center gap-1.5 font-sans">
                      <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full" /> Local Groups
                    </div>
                    <p className="leading-relaxed text-[11px] text-gray-500 font-sans">
                      We group businesses dynamically by industry and region so you see relevant, accurate performance comparisons.
                    </p>
                  </div>

                  <div className="p-3.5 bg-slate-50 rounded-xl border border-gray-200/60 space-y-1">
                    <div className="text-gray-950 font-bold uppercase tracking-tight text-xs flex items-center gap-1.5 font-sans">
                      <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full" /> Full Privacy
                    </div>
                    <p className="leading-relaxed text-[11px] text-gray-500 font-sans">
                      All comparative data is completely anonymized. Staff rosters, names, and specific business identities are never shared.
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-950">Key Tips</h4>
                  
                  <div className="space-y-2">
                    <div className="p-3 bg-indigo-50/40 hover:bg-indigo-50/70 border border-indigo-100/50 rounded-xl transition-all space-y-1">
                      <strong className="text-slate-900 text-xs block">1. Maintain accurate timezones</strong>
                      <p className="text-[11px] text-slate-600">Ensure your settings match your work location to cleanly align daily transaction records with payroll.</p>
                    </div>

                    <div className="p-3 bg-indigo-50/40 hover:bg-indigo-50/70 border border-indigo-100/50 rounded-xl transition-all space-y-1">
                      <strong className="text-slate-900 text-xs block">2. Regular transaction registers</strong>
                      <p className="text-[11px] text-slate-600">Syncing sales data daily avoids mismatches and lowers the frequency of staff disputes.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: REFERRAL LOOP */}
          {activeTab === 'referrals' && (
            <div className="bg-amber-50/50 p-6 rounded-2xl border border-amber-200/60 space-y-4">
              <div className="space-y-1.5 border-b border-amber-200/65 pb-4">
                <div className="flex items-center gap-2 text-amber-800">
                  <Gift className="w-5 h-5" />
                  <h3 className="text-xs font-black uppercase tracking-widest">Client Referral Program</h3>
                </div>
                <h4 className="text-base font-black text-amber-950 uppercase tracking-tight">
                  Share Your Link, Earn 10% Lifetime Billing Credits
                </h4>
                <p className="text-[11px] text-amber-900 font-medium leading-relaxed">
                  TipFlow grows alongside premium hospitality operators. Invite neighboring cafés, bars, restaurants, or hotels to join our system!
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                <div className="p-3.5 bg-white rounded-xl border border-amber-100">
                  <span className="font-extrabold text-xs text-amber-950 block uppercase tracking-tight">1. Grab Custom Code</span>
                  <p className="text-[10px] text-gray-500 leading-relaxed mt-1">
                    Navigate to your custom **Owner Portal** in Settings to copy your dedicated invitation link, QR code, or campaign flyer.
                  </p>
                </div>

                <div className="p-3.5 bg-white rounded-xl border border-amber-100">
                  <span className="font-extrabold text-xs text-amber-950 block uppercase tracking-tight">2. Invite Neighbors</span>
                  <p className="text-[10px] text-gray-500 leading-relaxed mt-1">
                    Your invited peers receive a 30-day free trial with full tip pooling onboarding and point-of-sale integration support.
                  </p>
                </div>

                <div className="p-3.5 bg-white rounded-xl border border-amber-100 font-semibold text-emerald-800">
                  <span className="font-extrabold text-xs text-emerald-950 block uppercase tracking-tight">3. Earn 10% Monthly Credit</span>
                  <p className="text-[10px] text-emerald-700/80 leading-relaxed mt-1">
                    Receive a **10% recurring billing credit** off your subscription invoice each month for the lifetime of their active paid account.
                  </p>
                </div>
              </div>

              <div className="text-[10px] text-amber-850/80 font-bold text-center italic mt-2">
                * Zero caps on referral credits. Stack multiple peer referrals to offset up to 100% of your TipFlow monthly software subscription!
              </div>
            </div>
          )}

          {/* TAB 4: FAQ */}
          {activeTab === 'faq' && (
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <h3 className="text-xs font-black text-gray-900 uppercase tracking-widest border-b border-gray-100 pb-3 flex items-center gap-1.5">
                <HelpCircle className="w-4 h-4 text-indigo-600" /> Frequently Asked Questions
              </h3>

              <div className="space-y-4 divide-y divide-gray-100">
                <div className="pt-1 first:pt-0 space-y-1">
                  <h4 className="font-extrabold text-xs text-gray-900 uppercase tracking-tight">How do I fix a staff spreadsheet hour error?</h4>
                  <p className="text-[11px] text-gray-500 leading-relaxed font-semibold">
                    Always approve staff roster adjustments before finalizing exports. Modifying details in the central "People Dashboard" immediately triggers auto-reconciliation and repairs payout imbalances.
                  </p>
                </div>

                <div className="pt-3 space-y-1">
                  <h4 className="font-extrabold text-xs text-gray-900 uppercase tracking-tight">Are Cash Tips reconciled with my POS files?</h4>
                  <p className="text-[11px] text-gray-500 leading-relaxed font-semibold">
                    No. Cash submittals operate on an independent register to comply with strict state policies. However, Cash splits reuse shift hours logged in your POS logs to maintain consistent pooling calculations.
                  </p>
                </div>

                <div className="pt-3 space-y-1">
                  <h4 className="font-extrabold text-xs text-gray-900 uppercase tracking-tight">How do disputes affect payroll operations?</h4>
                  <p className="text-[11px] text-gray-500 leading-relaxed font-semibold">
                    Disputed items are flagged immediately. Pending items remain highlighted inside details but are locked from final export runs until manager approvals resolve the discrepancy.
                  </p>
                </div>
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>

      {/* Footer Info Strip */}
      <div className="text-[10px] text-gray-400 font-semibold text-center mt-6 flex items-center justify-center gap-2">
        <span>Google Cloud Security Certified</span> • <span>ISO-27001 Certified Vault</span> • <span>Immutable Auditing Active</span>
      </div>

    </div>
  );
};
